import { apiFetch, ApiError } from './client';

export type Platform = 'x' | 'youtube' | 'instagram' | 'tiktok' | 'other';

export interface ThemeItem {
  rank: number;
  title: string;
  examples: string[];
  whyItWorks: string;
  adaptationForNatuCare: string;
  bestChannel: string;
}

export interface PatternItem {
  name: string;
  whatItIs: string;
  whyItWorks: string;
  howForUs: string;
}

export interface YouTubeOpportunity {
  title: string;
  description: string;
  insight: string;
  format: string;
}

export interface ShortFormOpportunity {
  hook: string;
  format: string;
  insight: string;
}

export interface UgcIdeaItem {
  title: string;
  description: string;
  format: string;
  insight: string;
}

export interface PriorityItem {
  rank: number;
  title: string;
  why: string;
  format: string;
  adaptation: string;
}

export interface ReportSource {
  url: string;
  note: string;
}

export interface ContentReport {
  _id: string;
  handle: string;
  platform: Platform;
  periodFrom: string;
  periodTo: string;
  generatedAt: string;
  source: 'llm' | 'unavailable';
  model: string | null;
  status: 'ok' | 'unavailable';
  statusReason: string | null;
  profileContext: string;
  executiveSummary: {
    topThemes: string[];
    workingFormats: string[];
    engagementPattern: string;
  };
  topThemes: ThemeItem[];
  contentPatterns: PatternItem[];
  opportunities: {
    youtube: YouTubeOpportunity[];
    meta: ShortFormOpportunity[];
    shortForm: ShortFormOpportunity[];
  };
  ugcIdeas: UgcIdeaItem[];
  hooks: string[];
  priorities: PriorityItem[];
  strategicTakeaways: {
    whatThisProfileTeaches: string[];
    whatBrandsMiss: string[];
    lessonsToImplement: string[];
  };
  earlySignals: string[];
  sources: ReportSource[];
}

export interface ContentReportListItem {
  _id: string;
  handle: string;
  platform: Platform;
  periodFrom: string;
  periodTo: string;
  generatedAt: string;
  status: 'ok' | 'unavailable';
  source: 'llm' | 'unavailable';
}

export type Cadence = 'weekly' | 'biweekly';

export interface WatchedProfile {
  handle: string;
  platform: Platform;
  note: string;
}

export interface ContentRadarConfig {
  profiles: WatchedProfile[];
  insightTypes: string[];
  adaptationInstruction: string;
  cadence: Cadence;
  active: boolean;
}

export function getConfig(signal?: AbortSignal) {
  return apiFetch<{ config: ContentRadarConfig }>('/api/content/config', { signal });
}

export function saveConfig(config: ContentRadarConfig) {
  return apiFetch<{ config: ContentRadarConfig }>('/api/content/config', { method: 'PUT', body: config });
}

export function listReports(opts: { handle?: string; before?: string; limit?: number } = {}, signal?: AbortSignal) {
  const params = new URLSearchParams();
  if (opts.handle) params.set('handle', opts.handle);
  if (opts.before) params.set('before', opts.before);
  if (opts.limit) params.set('limit', String(opts.limit));
  const qs = params.toString();
  return apiFetch<{ reports: ContentReportListItem[] }>(`/api/content/reports${qs ? `?${qs}` : ''}`, { signal });
}

export function getReport(id: string, signal?: AbortSignal) {
  return apiFetch<{ report: ContentReport }>(`/api/content/reports/${encodeURIComponent(id)}`, { signal });
}

export function generateReport(input: { handle: string; platform: Platform; adaptationInstruction?: string }) {
  return apiFetch<{ report: ContentReport }>('/api/content/generate', { method: 'POST', body: input });
}

export async function downloadReportPdf(id: string): Promise<Blob> {
  const res = await fetch(`/api/content/reports/${encodeURIComponent(id)}/pdf`, { credentials: 'include' });
  if (!res.ok) throw new ApiError(res.status, null, res.statusText);
  return res.blob();
}
